import React, { useEffect, useState } from "react";
import api, { money, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ClipboardCheck, Package, FileWarning, AlertTriangle, Receipt, ShieldAlert, History } from "lucide-react";
import { toast } from "sonner";

const STAGE_TONE = {
  "Assigned": "bg-slate-100 text-slate-700", "Accepted": "bg-blue-100 text-blue-700",
  "Ready to Start": "bg-cyan-100 text-cyan-700", "In Progress": "bg-indigo-100 text-indigo-700",
  "Change Order Needed": "bg-amber-100 text-amber-800", "Ready for Review": "bg-purple-100 text-purple-700",
  "Completed": "bg-emerald-100 text-emerald-700", "Payout Approved": "bg-teal-100 text-teal-700", "Paid": "bg-green-100 text-green-700",
};
const PAYOUT_TONE = { "Paid": "text-green-700", "Approved for Payment": "text-blue-700", "Under SJ Review": "text-purple-700", "Not Eligible": "text-slate-500", "Pending Documentation": "text-amber-700" };
const MAT_TONE = { "Pending Approval": "bg-amber-100 text-amber-800", "Approved": "bg-blue-100 text-blue-700", "Denied": "bg-rose-100 text-rose-700", "Purchased": "bg-emerald-100 text-emerald-700" };
const CO_TONE = { "Pending": "bg-amber-100 text-amber-800", "Approved": "bg-blue-100 text-blue-700", "Denied": "bg-rose-100 text-rose-700", "Completed": "bg-emerald-100 text-emerald-700" };

export function AdminCompliancePanel({ wo, onDone }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [c, setC] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [ovStage, setOvStage] = useState("");
  const [ovReason, setOvReason] = useState("");
  const [showLog, setShowLog] = useState(false);

  const load = () => {
    api.get(`/work-orders/${wo.id}/compliance`).then(({ data }) => setC(data)).catch(() => setC(false));
    api.get(`/admin/work-orders/${wo.id}/compliance-log`).then(({ data }) => setLog(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [wo.id]); // eslint-disable-line

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); load(); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    setBusy(false);
  };

  if (c === null) return <div className="bg-white border border-slate-200 rounded-xl p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>;
  if (!c) return null;

  const reviewing = c.stage === "Ready for Review";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4" data-testid="admin-compliance-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-blue-600" /> SJ Review & Compliance</h3>
        <span data-testid="admin-compliance-stage" className={`text-xs font-bold uppercase px-2 py-1 rounded ${STAGE_TONE[c.effective_stage || c.stage] || "bg-slate-100 text-slate-700"}`}>{c.effective_stage || c.stage}</span>
      </div>
      <div className="text-sm text-slate-500">Payout status: <span className={`font-semibold ${PAYOUT_TONE[c.payout_stage] || "text-slate-600"}`} data-testid="admin-payout-stage">{c.payout_stage}</span></div>

      {/* Completion review */}
      {reviewing ? (
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 space-y-2" data-testid="completion-review">
          <div className="text-sm font-semibold text-purple-800">Job submitted for review</div>
          {c.completion_notes && <div className="text-xs text-slate-600">Notes: {c.completion_notes}</div>}
          <Textarea data-testid="review-note" value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} rows={2} placeholder="Reason (required to request corrections or reject)" className="bg-white" />
          <div className="grid grid-cols-1 gap-2">
            <Button data-testid="approve-completion-btn" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => run(() => api.post(`/admin/work-orders/${wo.id}/review`, { action: "approve_completion", note: reviewNote }), "Completion approved")}>Approve Completion</Button>
            <div className="grid grid-cols-2 gap-2">
              <Button data-testid="request-corrections-btn" disabled={busy} variant="outline" className="text-amber-700 border-amber-300"
                onClick={() => reviewNote.trim() ? run(() => api.post(`/admin/work-orders/${wo.id}/review`, { action: "request_corrections", note: reviewNote }), "Corrections requested") : toast.error("Enter a reason")}>Request Corrections</Button>
              <Button data-testid="reject-completion-btn" disabled={busy} variant="outline" className="text-rose-700 border-rose-300"
                onClick={() => reviewNote.trim() ? run(() => api.post(`/admin/work-orders/${wo.id}/review`, { action: "reject_completion", note: reviewNote }), "Completion rejected") : toast.error("Enter a reason")}>Reject</Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-xs text-slate-400">No completion awaiting review. Actions below stay available.</div>
      )}

      {/* Materials */}
      <Section title="Material Requests" icon={Package} count={c.material_requests.length}>
        {c.material_requests.map((m) => (
          <div key={m.id} data-testid={`admin-material-${m.id}`} className="border border-slate-200 rounded-lg p-3 text-sm">
            <div className="flex items-center justify-between"><span className="font-medium text-slate-800">{m.item} · {money(m.estimated_cost)}</span><span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${MAT_TONE[m.status]}`}>{m.status}</span></div>
            {m.vendor && <div className="text-xs text-slate-500">Vendor: {m.vendor}{m.reason ? ` · ${m.reason}` : ""}</div>}
            {m.photo_path && <a href={fileUrl(m.photo_path)} target="_blank" rel="noreferrer" className="text-xs text-blue-600">View estimate photo</a>}
            {m.receipt_path && <a href={fileUrl(m.receipt_path)} target="_blank" rel="noreferrer" className="text-xs text-blue-600 flex items-center gap-1"><Receipt className="w-3 h-3" /> Receipt</a>}
            {isAdmin && m.status === "Pending Approval" && (
              <div className="flex gap-2 mt-2">
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`material-approve-${m.id}`} disabled={busy} onClick={() => run(() => api.post(`/admin/material-requests/${m.id}/action`, { action: "approve" }), "Material approved")}>Approve</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600 border-rose-200" data-testid={`material-deny-${m.id}`} disabled={busy} onClick={() => run(() => api.post(`/admin/material-requests/${m.id}/action`, { action: "deny" }), "Material denied")}>Deny</Button>
              </div>
            )}
          </div>
        ))}
      </Section>

      {/* Change orders */}
      <Section title="Change Orders" icon={FileWarning} count={c.change_orders.length}>
        {c.change_orders.map((o) => (
          <div key={o.id} data-testid={`admin-change-order-${o.id}`} className="border border-slate-200 rounded-lg p-3 text-sm">
            <div className="flex items-center justify-between"><span className="font-medium text-slate-800">{o.description}</span><span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${CO_TONE[o.status]}`}>{o.status}</span></div>
            <div className="text-xs text-slate-500">{o.reason} · +Labor {money(o.additional_labor)} · +Material {money(o.additional_material)}</div>
            {o.photo_path && <a href={fileUrl(o.photo_path)} target="_blank" rel="noreferrer" className="text-xs text-blue-600">View photo</a>}
            <div className="flex gap-2 mt-2">
              {o.status === "Pending" && <>
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`co-approve-${o.id}`} disabled={busy} onClick={() => run(() => api.post(`/admin/change-orders/${o.id}/action`, { action: "approve" }), "Change order approved")}>Approve</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600 border-rose-200" data-testid={`co-deny-${o.id}`} disabled={busy} onClick={() => run(() => api.post(`/admin/change-orders/${o.id}/action`, { action: "deny" }), "Change order denied")}>Deny</Button>
              </>}
              {o.status === "Approved" && <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`co-complete-${o.id}`} disabled={busy} onClick={() => run(() => api.post(`/admin/change-orders/${o.id}/action`, { action: "complete" }), "Change order completed")}>Mark Completed</Button>}
            </div>
          </div>
        ))}
      </Section>

      {/* Issues */}
      <Section title="Reported Issues" icon={AlertTriangle} count={c.issues.length}>
        {c.issues.map((i) => (
          <div key={i.id} data-testid={`admin-issue-${i.id}`} className="border border-slate-200 rounded-lg p-3 text-sm">
            <div className="flex items-center justify-between"><span className="font-medium text-slate-800">{i.issue_type}</span><span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{i.status}</span></div>
            <div className="text-xs text-slate-500">{i.notes}</div>
            {i.photo_path && <a href={fileUrl(i.photo_path)} target="_blank" rel="noreferrer" className="text-xs text-blue-600">View photo</a>}
            {i.status === "Open" && <Button size="sm" variant="outline" className="h-7 text-xs mt-2" data-testid={`issue-resolve-${i.id}`} disabled={busy} onClick={() => run(() => api.post(`/admin/issues/${i.id}/resolve`), "Issue resolved")}>Resolve</Button>}
          </div>
        ))}
      </Section>

      {/* Customer sign-off toggle */}
      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
        <span className="text-sm text-slate-700">Require final customer sign-off</span>
        <Switch data-testid="signoff-toggle" checked={!!c.customer_signoff_required} disabled={busy}
          onCheckedChange={(v) => run(() => api.post(`/admin/work-orders/${wo.id}/toggle-signoff`, { required: v }), v ? "Sign-off required" : "Sign-off not required")} />
      </div>
      {c.customer_signoff?.path && <a href={fileUrl(c.customer_signoff.path)} target="_blank" rel="noreferrer" className="text-xs text-emerald-600" data-testid="admin-signoff-link">View uploaded sign-off</a>}

      {/* Stage override (admin) */}
      {isAdmin && (
        <div className="border-t border-slate-100 pt-3 space-y-2">
          <div className="text-xs font-bold uppercase text-slate-400 flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> Override Stage</div>
          <Select value={ovStage} onValueChange={setOvStage}>
            <SelectTrigger data-testid="override-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
            <SelectContent>{c.stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea data-testid="override-reason" value={ovReason} onChange={(e) => setOvReason(e.target.value)} rows={2} placeholder="Reason (required)" />
          <Button data-testid="override-apply-btn" size="sm" variant="outline" disabled={busy}
            onClick={() => (ovStage && ovReason.trim()) ? run(() => api.post(`/admin/work-orders/${wo.id}/override-stage`, { new_stage: ovStage, reason: ovReason }), "Stage overridden").then(() => { setOvStage(""); setOvReason(""); }) : toast.error("Select a stage and enter a reason")}>Apply Override</Button>
        </div>
      )}

      {/* Compliance log */}
      <div className="border-t border-slate-100 pt-3">
        <button data-testid="toggle-compliance-log" onClick={() => setShowLog((s) => !s)} className="text-sm text-slate-600 flex items-center gap-1"><History className="w-3.5 h-3.5" /> Compliance Log ({log.length})</button>
        {showLog && (
          <ol className="mt-2 space-y-2" data-testid="compliance-log">
            {log.map((l) => (
              <li key={l.id} className="text-xs">
                <div className="text-slate-700 font-medium">{l.event}{l.override ? <span className="ml-1 text-[10px] font-bold uppercase text-amber-700">override</span> : null}</div>
                <div className="text-slate-400">{l.by_name} · {l.by_role} · {new Date(l.at).toLocaleString()}</div>
                {l.reason && <div className="text-slate-500">Reason: {l.reason}</div>}
                {l.prev_stage && l.new_stage && <div className="text-slate-500">{l.prev_stage} → {l.new_stage}</div>}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <div className="border-t border-slate-100 pt-3">
      <div className="text-xs font-bold uppercase text-slate-400 mb-2 flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {title} ({count})</div>
      {count === 0 ? <p className="text-xs text-slate-400">None.</p> : <div className="space-y-2">{children}</div>}
    </div>
  );
}
